import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import {
  PEAK_COUNT,
  computeRmsPeaks,
  parseSampleData,
  placeholderPeaks,
} from "./waveformPeaks";

// Safari keeps AudioContext suspended until a user gesture resumes it.
// This one-time listener ensures any suspended context gets resumed on first click.
let safariAudioUnlocked = false;
function ensureSafariAudioUnlock() {
  if (safariAudioUnlocked) return;
  const unlock = () => {
    safariAudioUnlocked = true;
    if (window._wavesurferAudioCtx && window._wavesurferAudioCtx.state === "suspended") {
      window._wavesurferAudioCtx.resume();
    }
    document.removeEventListener("click", unlock, true);
    document.removeEventListener("touchstart", unlock, true);
  };
  document.addEventListener("click", unlock, true);
  document.addEventListener("touchstart", unlock, true);
}

function buildGradients() {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const waveColor = ctx.createLinearGradient(0, 0, 0, canvas.height * 1.5);
  waveColor.addColorStop(0, "#656666");
  waveColor.addColorStop((canvas.height * 0.7) / canvas.height, "#656666");
  waveColor.addColorStop((canvas.height * 0.7 + 1) / canvas.height, "#ffffff");
  waveColor.addColorStop((canvas.height * 0.7 + 2) / canvas.height, "#ffffff");
  waveColor.addColorStop((canvas.height * 0.7 + 3) / canvas.height, "#B1B1B1");
  waveColor.addColorStop(1, "#B1B1B1");

  const progressColor = ctx.createLinearGradient(0, 0, 0, canvas.height * 0.3);
  progressColor.addColorStop(0, "#EE772F");
  progressColor.addColorStop(1, "#7C4DFF");

  return { waveColor, progressColor };
}

/**
 * Creates a WaveSurfer instance for `url` and starts playback immediately.
 *
 * Playback must start synchronously with the song being selected: the browser
 * is only willing to autoplay for a short window after the user's click.
 * So we never wait on a download before calling play(). The media element
 * streams the file progressively; the waveform is drawn from the backend's
 * `sampleData` when available, or a placeholder that is replaced with real
 * RMS peaks in the background once the song is already playing.
 *
 * With `autoplay: false` (a player rebuilt for a song the listener did not
 * just pick) the song loads and waits for the play button.
 */
export default function useWavesurfer(containerRef, url, sampleData, { autoplay = true, onMediaError, startAt = 0 } = {}) {
  const [wavesurfer, setWavesurfer] = useState(null);
  // Read when a song loads; changing them never reloads the song.
  const autoplayRef = useRef(autoplay);
  autoplayRef.current = autoplay;
  const onMediaErrorRef = useRef(onMediaError);
  onMediaErrorRef.current = onMediaError;
  const startAtRef = useRef(startAt);
  startAtRef.current = startAt;

  useEffect(() => {
    if (!containerRef.current || !url) return undefined;
    ensureSafariAudioUnlock();

    // Audio element without crossOrigin, so any URL plays regardless of CORS.
    const audio = document.createElement("audio");
    audio.preload = "auto";

    const ws = WaveSurfer.create({
      media: audio,
      ...buildGradients(),
      barWidth: 3,
      barGap: 2,
      barRadius: 2,
      barHeight: 0.7,
      height: 30,
      normalize: true,
      container: containerRef.current,
    });

    const backend = ws.getMediaElement();
    if (backend && backend._audioContext) {
      window._wavesurferAudioCtx = backend._audioContext;
    }

    let cancelled = false;
    const abortCtrl = new AbortController();
    const knownPeaks = parseSampleData(sampleData);

    // A signed audio link that expired fails here (HTTP 403); the caller can
    // swap in a fresh link, which reloads the song from where it stopped.
    const onAudioError = () => {
      if (cancelled || !audio.error) return;
      onMediaErrorRef.current?.({ code: audio.error.code, currentTime: audio.currentTime || 0 });
    };
    audio.addEventListener("error", onAudioError);
    if (startAtRef.current > 0) {
      const resumeAt = startAtRef.current;
      ws.once("ready", () => {
        if (!cancelled) ws.setTime(resumeAt);
      });
    }

    // 1. Start streaming and playing right away.
    ws.load(url, [knownPeaks || placeholderPeaks(PEAK_COUNT)]).catch((e) => {
      if (!cancelled && e?.name !== "AbortError") {
        console.error("[WaveSurfer] Failed to load audio", e);
      }
    });
    if (autoplayRef.current) {
      ws.play().catch((e) => {
        // NotAllowedError = autoplay blocked; the user can press play.
        // AbortError = the song changed before playback started.
        if (!cancelled && e?.name !== "AbortError") {
          console.warn("[WaveSurfer] Autoplay was blocked", e?.name);
        }
      });
    }

    // 2. If the backend has no peaks, compute real ones after playback began.
    //    Reloading the same URL keeps the media element (and playback) intact
    //    and only redraws the waveform.
    const computeRealPeaks = async () => {
      try {
        const response = await fetch(url, { signal: abortCtrl.signal });
        const arrayBuffer = await response.arrayBuffer();
        if (cancelled) return;

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioCtx();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        audioCtx.close();
        if (cancelled) return;

        await ws.load(url, [computeRmsPeaks(audioBuffer, PEAK_COUNT)], ws.getDuration());
      } catch (e) {
        // CORS or network error: keep the placeholder waveform.
        if (cancelled || e?.name === "AbortError") return;
      }
    };
    let unsubReady = null;
    if (!knownPeaks) {
      unsubReady = ws.once("ready", () => {
        if (!cancelled) computeRealPeaks();
      });
    }

    setWavesurfer(ws);

    return () => {
      cancelled = true;
      abortCtrl.abort();
      if (unsubReady) unsubReady();
      audio.removeEventListener("error", onAudioError);
      audio.pause();
      audio.src = "";
      ws.destroy();
    };
    // sampleData belongs to the same song as url, so url alone drives reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, url]);

  return wavesurfer;
}
